<script lang="ts">
  import { CodeSnippet, Link } from 'carbon-components-svelte';

  import type {
    AttributeOptions,
    BuildEnv,
    EmbedValue,
    SrcMethod,
    TestingQueryAttributes,
  } from '../modules/attributes';
  import { camelToSnakeCase, snakeCaseAttributes } from '../modules/attributes';
  import ScriptMethod from './ScriptMethod.svelte';

  type Props = {
    attributes: AttributeOptions & TestingQueryAttributes;
    buildEnv: BuildEnv;
    embedValue: EmbedValue;
    srcMethod: SrcMethod;
  };

  const { attributes, buildEnv, embedValue, srcMethod }: Props = $props();

  function videoRoot(src: string, build: string) {
    if (build === 'dev') {
      if (import.meta.env.DEV) {
        return src === 'module' ? 'video-dev.html?' : 'video-dev-script.html?';
      }

      return 'video-dev-script.html?';
    }

    if (build === 'prod') {
      return 'video.html?';
    }

    return 'video-staging.html?';
  }

  function createSrcToRender(attr = attributes, src = srcMethod, build = buildEnv, omitOrigin = false) {
    return `${(Object.keys(attr) as Array<keyof typeof attr>).reduce((agg, key) => {
      if (attr[key] !== undefined && attr[key] !== null && attr[key] !== '') {
        const queryKey = snakeCaseAttributes.includes(key) ? camelToSnakeCase(key) : key;
        if (!agg) {
          return omitOrigin ? `${queryKey}=${attr[key]}` : `${videoRoot(src, build)}` + `${queryKey}=${attr[key]}`;
        }

        return agg + `&${queryKey}=${attr[key]}`;
      }

      return agg;
    }, '')}`;
  }

  const srcToRender = $derived(createSrcToRender(attributes, srcMethod, buildEnv));
  const paramsToRender = $derived(createSrcToRender(attributes, srcMethod, buildEnv, true));
</script>

{#if embedValue === 'iframe'}
  <div style="position:relative;overflow:hidden;padding-top:56.25%;">
    <iframe
      allow="autoplay; fullscreen"
      allowfullscreen={true}
      data-testid="iframe--default"
      src={`${srcToRender}`}
      style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;"
      title="iframe" />
  </div>
{/if}
{#if embedValue === 'alt'}
  <iframe
    allow="autoplay; fullscreen"
    allowfullscreen={true}
    data-testid="iframe--alt"
    src={`${srcToRender}`}
    style="width:100%;height:100%;border:none;"
    title="iframe alt" />
{/if}

{#if embedValue === 'script'}
  {#key attributes}
    <ScriptMethod build={buildEnv} {attributes} />
  {/key}
{/if}
<Link data-testid="link--standalone" href={srcToRender} size="lg" style="margin: 1rem 0;" target="_blank"
  >Open Standalone</Link>
<CodeSnippet code={paramsToRender} type="multi" wrapText />
